/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/card";
import { PacketLossMonitor } from "@/types/types";
import { getPacketLossHistory } from "@/api/packetloss";
import { MonitorStatusCard } from "./components/MonitorStatusCard";
import { MonitorPerformanceChart } from "./components/MonitorPerformanceChart";
import { MonitorResultsTable } from "./components/MonitorResultsTable";
import { MonitorStatus } from "./types/monitorStatus";
import { PACKET_LOSS_HISTORY_PAGE_SIZE } from "./constants/packetLossConstants";

interface PacketLossMonitorDetailsProps {
  selectedMonitor: PacketLossMonitor;
  monitorStatuses: Map<number, MonitorStatus>;
  onTraceRoute?: () => void;
}

export const PacketLossMonitorDetails: React.FC<
  PacketLossMonitorDetailsProps
> = ({ selectedMonitor, monitorStatuses, onTraceRoute }) => {
  const {
    data: monitorHistory,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["packetloss", "history", selectedMonitor.id],
    queryFn: ({ pageParam }) =>
      getPacketLossHistory(
        selectedMonitor.id,
        pageParam,
        PACKET_LOSS_HISTORY_PAGE_SIZE,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (total, page) => total + page.data.length,
        0,
      );

      return loadedCount < (lastPage.total || 0)
        ? lastPage.page + 1
        : undefined;
    },
    staleTime: 5000, // Consider data stale after 5 seconds
    refetchInterval: false, // Don't refetch automatically
  });

  const historyList =
    monitorHistory?.pages.flatMap((page) => page.data) || [];
  const totalHistoryCount = monitorHistory?.pages[0]?.total || historyList.length;
  const hasMoreHistory = hasNextPage || false;

  // How many recent tests the latency/jitter chart shows. When the user picks a
  // wider window than we've loaded, pull more history pages to fill it.
  const [chartPoints, setChartPoints] = useState(30);
  useEffect(() => {
    if (historyList.length < chartPoints && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [chartPoints, historyList.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const status = monitorStatuses.get(selectedMonitor.id);
  const showStatus =
    selectedMonitor.enabled &&
    status &&
    (status.isRunning || status.packetLoss !== undefined);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05, ease: [0.23, 1, 0.32, 1] }}
      className="flex-1"
    >
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {selectedMonitor.name || selectedMonitor.host} Details
          </h2>
          {onTraceRoute && (
            <motion.button
              onClick={onTraceRoute}
              className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500/20"
              title="Run traceroute to this host"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <GlobeAltIcon className="w-3 h-3" />
              <span>Trace Route</span>
            </motion.button>
          )}
        </div>

        {/* Current Status - Only show when actively testing or has recent results */}
        {showStatus && (
          <MonitorStatusCard
            status={status}
            threshold={selectedMonitor.threshold}
          />
        )}

        {/* Performance Chart */}
        <MonitorPerformanceChart
          historyList={historyList}
          selectedMonitorId={selectedMonitor.id}
          points={chartPoints}
          onPointsChange={setChartPoints}
        />

        {/* Recent Results */}
        <MonitorResultsTable
          historyList={historyList}
          selectedMonitor={selectedMonitor}
          totalCount={totalHistoryCount}
          hasMore={hasMoreHistory}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </Card>
    </motion.div>
  );
};
